import { useGetSellerCustomers } from '../../../api/lending/lapiHooks';
import {
  deriveResultRows,
  SearchResult,
} from '../components/CustomersSearch/deriveResultRows';
import { CustomerRow } from '../CustomersV2.types';
import { useDebouncedValue } from './useDebouncedValue';

interface CustomersSearchResult {
  results: SearchResult[];
  loading: boolean;
  debouncedTerm: string;
}

/**
 * The results overlay shows all matches in one scrollable list (no pagination),
 * so request a large page to avoid silently truncating to the API's default
 * page size. A seller's matching-buyer count for a name substring is small in
 * practice; this ceiling just prevents an unbounded fetch.
 */
const SEARCH_PAGE_SIZE = 50;

export function useCustomersSearch(
  term: string,
  { enabled = true }: { enabled?: boolean } = {},
): CustomersSearchResult {
  const debouncedTerm = useDebouncedValue(term.trim(), 300);
  // Only fetch when the search is active AND the caller wants it (e.g. the
  // overlay is open). This stops a term seeded from the URL — box shown but
  // overlay closed on landing — from firing a redundant background request.
  const isActive = enabled && debouncedTerm.length > 0;

  const { sellerCustomers, loadingSellerCustomers } = useGetSellerCustomers(
    {
      search: isActive ? debouncedTerm : undefined,
      page_size: SEARCH_PAGE_SIZE,
    },
    { enabled: isActive },
  );

  const results = isActive
    ? deriveResultRows((sellerCustomers ?? []) as CustomerRow[])
    : [];

  return {
    results,
    loading: isActive && loadingSellerCustomers,
    debouncedTerm,
  };
}
